import { GEMS, MARKET } from '../config/constants';
import { CHEESES, freshnessMultiplier, type Rarity } from '../data/cheeses';
import { CHEESE_PRICE_BANDS, PRICE_BANDS_BY_MARKET_KEY, PRICING_REVISION, pricePerPound } from '../data/pricing';
import type { SaveState } from './GameState';

export interface RetailBand {
  unit: string;
  min: number;
  max: number;
  source: string;
  asOf: string;
  latest?: number;
}

export interface WholesaleRow {
  price: number;
  salesLbs: number;
}

export interface MarketSnapshot {
  generatedAt: string;
  region: string;
  currency: string;
  stale: boolean;
  notes?: string[];
  retail: Record<string, RetailBand>;
  wholesale: { weekEnding: string; [product: string]: WholesaleRow | string };
  depth: Record<string, number>;
}

/** Fixed retail bands have priority over every market-runner snapshot. */
export const REFERENCE_RETAIL: Readonly<Record<string, RetailBand>> = Object.freeze(Object.fromEntries(
  Object.values(CHEESE_PRICE_BANDS).map((band) => [band.marketKey, Object.freeze({
    unit: 'lb', min: band.floorPricePerLb, max: band.ceilingPricePerLb,
    source: band.source, asOf: PRICING_REVISION,
  })]),
));

/** Fallback demand/wholesale data plus the approved Phase 1D retail bands. */
export const SEED_SNAPSHOT: MarketSnapshot = {
  generatedAt: '2026-09-18T00:00:00Z',
  region: 'US',
  currency: 'USD',
  stale: true,
  notes: ['Cheese prices use the fixed Phase 1D sheet reference. Demand and whey use fallback market data.'],
  retail: { ...REFERENCE_RETAIL },
  wholesale: {
    weekEnding: '2026-09-05',
    cheddar_block_40lb: { price: 1.6095, salesLbs: 9596049 },
    dry_whey: { price: 0.6567, salesLbs: 7253880 },
  },
  depth: {
    mozzarella_fresh: 60,
    ricotta: 80,
    taleggio: 30,
    gouda: 40,
    havarti: 40,
    cheddar_farmstead: 50,
    emmental: 30,
    parmesan: 25,
    pecorino_romano: 25,
    roquefort: 15,
    gorgonzola: 20,
    whey: 200,
  },
};

export function withReferencePrices(snapshot: MarketSnapshot): MarketSnapshot {
  return {
    ...snapshot,
    retail: { ...snapshot.retail, ...REFERENCE_RETAIL },
    depth: { ...SEED_SNAPSHOT.depth, ...snapshot.depth },
  };
}

export async function loadMarketSnapshot(): Promise<MarketSnapshot> {
  // A static host that cannot serve data/market.json (the claude.ai artifact build,
  // see scripts/artifact-page.mjs) inlines the snapshot on the page instead.
  const inline = (globalThis as { __MARKET_SNAPSHOT__?: MarketSnapshot }).__MARKET_SNAPSHOT__;
  if (inline && inline.retail && inline.depth) return withReferencePrices(inline);
  try {
    const res = await fetch('./data/market.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = (await res.json()) as MarketSnapshot;
    if (!json.retail || !json.depth) throw new Error('Malformed snapshot');
    return withReferencePrices(json);
  } catch (err) {
    console.warn('Market snapshot unavailable, using seed', err);
    return SEED_SNAPSHOT;
  }
}

export function retailBand(snapshot: MarketSnapshot, marketKey: string): RetailBand | null {
  return REFERENCE_RETAIL[marketKey] ?? snapshot.retail[marketKey] ?? null;
}

/** Stars divide the retail price band: 1 star = min, 5 stars = max. Per pound. */
export function priceForStars(_snapshot: MarketSnapshot, marketKey: string, stars: number): number {
  const band = PRICE_BANDS_BY_MARKET_KEY[marketKey];
  if (!band) throw new Error(`Missing Phase 1D price band: ${marketKey}`);
  return pricePerPound(band, stars);
}

export function marketDepth(snapshot: MarketSnapshot, key: string): number {
  const depth = snapshot.depth[key];
  return Number.isFinite(depth) && depth > 0 ? depth : SEED_SNAPSHOT.depth[key] ?? MARKET.DEFAULT_DEPTH;
}

/** Selling into the market dilutes value in proportion to units already sold this week. */
export function dilution(state: SaveState, snapshot: MarketSnapshot, key: string): number {
  const sold = state.market.soldThisWeek[key] ?? 0;
  const depth = marketDepth(snapshot, key);
  return Math.max(MARKET.DILUTION_FLOOR, 1 - sold / depth);
}

export function wheyPricePerGallon(snapshot: MarketSnapshot): number {
  const row = snapshot.wholesale.dry_whey as WholesaleRow | undefined;
  const dryPrice = row?.price ?? 0.65;
  return dryPrice * MARKET.WHEY_DRY_LB_PER_GALLON;
}

export interface PriceBreakdown {
  /** Per pound at this star rating */
  base: number;
  pounds: number;
  freshness: number;
  aging: number;
  dilution: number;
  channel: number;
  negotiation: number;
  unit: number;
}

export function cheeseUnitPrice(
  state: SaveState,
  snapshot: MarketSnapshot,
  cheeseId: string,
  stars: number,
  ageDays: number,
  channel: 'wholesale' | 'retail',
  negotiation: number,
  agedMult = 1,
): PriceBreakdown {
  const def = CHEESES[cheeseId];
  if (!def) throw new Error(`Unknown cheese: ${cheeseId}`);
  const base = priceForStars(snapshot, def.marketKey, stars);
  const fresh = def.aging.ageable ? 1 : freshnessMultiplier(def, ageDays);
  const aging = def.aging.ageable ? agedMult : 1;
  const dil = dilution(state, snapshot, def.marketKey);
  const ch = channel === 'retail' ? MARKET.SHOP_CHANNEL : 1;
  const unit = base * def.unitPounds * fresh * aging * dil * ch * negotiation;
  return { base, pounds: def.unitPounds, freshness: fresh, aging, dilution: dil, channel: ch, negotiation, unit };
}

/** Exact pre-currency total, including each unit's position on the demand curve.
 * A quote never changes inventory or demand. Settle cents once when committing it.
 */
export function quoteCheeseSale(
  state: SaveState,
  snapshot: MarketSnapshot,
  cheeseId: string,
  stars: number,
  ageDays: number,
  channel: 'wholesale' | 'retail',
  negotiation: number,
  quantity: number,
  agedMult = 1,
): number {
  if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Cheese sale quantity must be a nonnegative whole number');
  const price = cheeseUnitPrice(state, snapshot, cheeseId, stars, ageDays, channel, negotiation, agedMult);
  const key = CHEESES[cheeseId].marketKey;
  const sold = state.market.soldThisWeek[key] ?? 0;
  const depth = marketDepth(snapshot, key);
  const beforeDemand = price.base * price.pounds * price.freshness * price.aging * price.channel * price.negotiation;
  let total = 0;
  for (let i = 0; i < quantity; i++) total += beforeDemand * Math.max(MARKET.DILUTION_FLOOR, 1 - (sold + i) / depth);
  return total;
}

export function gemChance(stars: number): number {
  const s = Math.min(5, Math.max(1, Math.round(stars)));
  return GEMS.CHANCE_BY_STARS[s] ?? 0;
}

/** Roll gems for `units` sold. Returns the total gem count. */
export function rollGems(stars: number, rarity: Rarity, units: number, rng: () => number = Math.random): number {
  const chance = gemChance(stars);
  const mult = GEMS.RARITY_MULT[rarity] ?? 1;
  let total = 0;
  for (let i = 0; i < units; i++) {
    if (rng() < chance) total += mult;
  }
  return total;
}
